import React from 'react';
import { Box, Paper, Typography, Button, CircularProgress } from '@mui/material';

import useChangeUsername from './useChangeUsername';
import { MuiStyledInput } from '../../shared/muiControls';
import RenderIf from '../../renderIf';

/**A component for changing the Username for another User in Cognito, used by Admin.
 * @param {string} userId The User ID related to the user in the database.
 * @param {Array} buttons An Array of React Elements (MUI Buttons) to display alongside the default save.
 * @param {function} callback A function to call to do any cleanup or necessary state changes once username has been changed successfully.
 * @returns {ReactNode} A ReactNode that renders the Change Username view.
 */
const ChangeUsername = ({ userId, buttons, callback }) => {
    const { isInitialized, isDoingAjax, currentUserName, newUserName, fetchUserData, updateNewUsername, changeUsername } =
        useChangeUsername(userId, callback);

    React.useEffect(() => {
        fetchUserData();
    }, []);

    return (
        <Box>
            <Paper
                sx={(theme) => ({
                    padding: theme.spacing(5),
                })}
            >
                <Box>
                    <Typography
                        sx={(theme) => ({
                            fontSize: '2rem',
                            borderBottom: `${theme.spacing(1 / 16)} solid black`,
                            marginBottom: theme.spacing(3),
                        })}
                    >
                        Change username
                    </Typography>
                </Box>

                <RenderIf isTrue={isInitialized}>
                    <Box
                        sx={(theme) => ({
                            display: 'flex',
                            flexDirection: 'column',
                            gap: theme.spacing(2),
                            mb: theme.spacing(3),
                        })}
                    >
                        <MuiStyledInput
                            id="current-username"
                            label="Current username"
                            value={currentUserName}
                            disabled
                            textFieldSxObj={(theme) => ({
                                width: '25rem',
                                [theme.breakpoints.down('xl')]: {
                                    width: '20rem',
                                },
                                [theme.breakpoints.down('lg')]: {
                                    width: '15rem',
                                },
                            })}
                        />

                        <MuiStyledInput
                            id="new-username"
                            label="New username"
                            type="email"
                            value={newUserName}
                            onChange={(event) => updateNewUsername(event.target.value)}
                            disabled={isDoingAjax}
                            textFieldSxObj={(theme) => ({
                                width: '25rem',
                                [theme.breakpoints.down('xl')]: {
                                    width: '20rem',
                                },
                                [theme.breakpoints.down('lg')]: {
                                    width: '15rem',
                                },
                            })}
                        />
                    </Box>

                    <RenderIf isTrue={typeof buttons === 'undefined'}>
                        <Button
                            variant="contained"
                            onClick={() => changeUsername()}
                            disabled={isDoingAjax}
                        >
                            Save
                        </Button>
                    </RenderIf>

                    <RenderIf isTrue={buttons?.length > 0 || React.isValidElement(buttons)}>
                        <Box
                            sx={(theme) => ({
                                display: 'flex',
                                gap: theme.spacing(2),
                                justifyContent: 'right',
                            })}
                        >
                            <RenderIf isTrue={buttons}>
                                {buttons.map((button) => {
                                    //Use the majority of the properties already defined in the array, but override/add the disabled prop
                                    return React.cloneElement(button, {
                                        disabled: isDoingAjax,
                                    });
                                })}
                            </RenderIf>
                            <Button
                                variant="contained"
                                onClick={() => changeUsername()}
                                disabled={isDoingAjax}
                            >
                                Save
                            </Button>
                        </Box>
                    </RenderIf>
                </RenderIf>

                <RenderIf isTrue={!isInitialized}>
                    <Typography
                        sx={(theme) => ({
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing(2),
                        })}
                    >
                        <CircularProgress size="2rem" /> Contacting Cognito server...
                    </Typography>
                </RenderIf>
            </Paper>
        </Box>
    );
};

export default ChangeUsername;
